// pipe: the ERP block of the sidebar, rendered from `ERP_NAV_ITEMS` in the user's saved order
// minus the sections they hid (`nav.layout.v1`, see erp-nav-items.tsx). The preference is loaded
// once per session; until it answers the default order renders, so the sidebar never blanks.
// Arrange mode: the "Customise navigation" entry (always present, so hidden sections can always
// be recovered) and a press-and-hold (450 ms, cancelled by movement) on any section open the one
// arrange sheet; desktop drag-to-reorder lives beside it.
import {
  type DNDData,
  DropIndicator,
  useDraggable,
  useDropTarget,
} from '@affine/component';
import { useI18n } from '@affine/i18n';
import { SettingsIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { MenuItem } from '../../modules/app-sidebar/views';
import { ErpService } from '../../modules/erp';
import {
  applyNavLayout,
  DEFAULT_NAV_ORDER,
  type NavId,
  type NavLayout,
  reorderNav,
} from '../../modules/erp/nav-layout';
import { KEY_NAV_LAYOUT } from '../../modules/erp/user-preferences';
import { CustomiseNavSheet } from './customise-nav-sheet';
import { ERP_NAV_ITEMS } from './erp-nav-items';

/** Press-and-hold duration before arrange mode opens (touch and mouse alike). */
export const HOLD_MS = 450;
/** Movement beyond this many pixels during the hold cancels it (a scroll, not a hold). */
const HOLD_SLOP_PX = 8;

export function useNavLayout(): NavLayout | null {
  const erp = useService(ErpService);
  const state = useLiveData(erp.preferences.pref<NavLayout>(KEY_NAV_LAYOUT));
  useEffect(() => {
    erp.preferences.load().catch(() => {});
  }, [erp]);
  return state.value;
}

/** Press-and-hold detection on a wrapper: returns the handlers and fires `onHold(id)`. */
function useHold(onHold: (id: NavId) => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }, []);
  useEffect(() => cancel, [cancel]);
  const onPointerDown = useCallback(
    (id: NavId, e: React.PointerEvent) => {
      cancel();
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = setTimeout(() => {
        timer.current = null;
        onHold(id);
      }, HOLD_MS);
    },
    [cancel, onHold]
  );
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const s = start.current;
      if (!s) return;
      if (
        Math.abs(e.clientX - s.x) > HOLD_SLOP_PX ||
        Math.abs(e.clientY - s.y) > HOLD_SLOP_PX
      ) {
        cancel();
      }
    },
    [cancel]
  );
  return { onPointerDown, onPointerMove, onPointerUp: cancel, onPointerCancel: cancel };
}

/** The drag payload of a sidebar section — our own shape, never a doc/collection entity, so no
 *  other drop target in the app accepts it and nothing else can be dropped on a section. */
type NavDND = DNDData<{ navId: NavId; from: 'pipe:nav' }, { navId: NavId }>;

function isNavDrag(data: unknown): data is NavDND['draggable'] {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { from?: unknown }).from === 'pipe:nav' &&
    typeof (data as { navId?: unknown }).navId === 'string'
  );
}

interface ErpNavRowProps {
  id: NavId;
  element: ReactElement;
  onReorder: (source: NavId, target: NavId, edge: 'top' | 'bottom') => void;
  hold: ReturnType<typeof useHold>;
  sheetOpen: boolean;
}

/** One section row: draggable (desktop pointer), a drop target for other sections, and the
 *  press-and-hold surface for arrange mode. */
function ErpNavRow({ id, element, onReorder, hold, sheetOpen }: ErpNavRowProps) {
  const { dragRef, dragging } = useDraggable<NavDND>(
    () => ({ data: { navId: id, from: 'pipe:nav' } }),
    [id]
  );
  const { dropTargetRef, closestEdge } = useDropTarget<NavDND>(
    () => ({
      data: { navId: id },
      canDrop: ({ source }) =>
        isNavDrag(source.data) && source.data.navId !== id,
      closestEdge: { allowedEdges: ['top', 'bottom'] },
      onDrop: ({ source, closestEdge: edge }) => {
        if (!isNavDrag(source.data)) return;
        if (edge !== 'top' && edge !== 'bottom') return;
        onReorder(source.data.navId, id, edge);
      },
    }),
    [id, onReorder]
  );
  return (
    <div
      data-nav-id={id}
      data-dragging={dragging}
      style={{ position: 'relative', opacity: dragging ? 0.5 : undefined }}
      ref={el => {
        dragRef.current = el;
        dropTargetRef.current = el;
      }}
      onPointerDown={e => hold.onPointerDown(id, e)}
      onPointerMove={hold.onPointerMove}
      onPointerUp={hold.onPointerUp}
      onPointerCancel={hold.onPointerCancel}
      onPointerLeave={hold.onPointerCancel}
      onContextMenu={e => {
        // a long touch also raises contextmenu on some browsers; the sheet owns it
        if (sheetOpen) e.preventDefault();
      }}
    >
      {element}
      {closestEdge && <DropIndicator edge={closestEdge} noTerminal />}
    </div>
  );
}

export function ErpNavList() {
  const t = useI18n();
  const erp = useService(ErpService);
  const layout = useNavLayout();
  const { visible } = applyNavLayout(DEFAULT_NAV_ORDER, layout);
  const byId = new Map(ERP_NAV_ITEMS.map(item => [item.id, item.element]));
  const [sheet, setSheet] = useState<{ open: boolean; focus?: NavId }>({
    open: false,
  });
  const openFor = useCallback(
    (id?: NavId) => setSheet({ open: true, focus: id }),
    [setSheet]
  );
  const hold = useHold(openFor);
  const onReorder = useCallback(
    (source: NavId, target: NavId, edge: 'top' | 'bottom') => {
      erp.preferences
        .save(KEY_NAV_LAYOUT, reorderNav(layout, source, target, edge, DEFAULT_NAV_ORDER))
        .catch(() => {});
    },
    [erp, layout]
  );

  return (
    <>
      {visible.map(id => {
        const element = byId.get(id);
        if (!element) return null;
        return (
          <ErpNavRow
            key={id}
            id={id}
            element={element}
            onReorder={onReorder}
            hold={hold}
            sheetOpen={sheet.open}
          />
        );
      })}
      <MenuItem
        icon={<SettingsIcon />}
        data-testid="pipe-nav-customise"
        onClick={() => openFor()}
      >
        {t['com.affine.pipe.nav.customise']()}
      </MenuItem>
      <CustomiseNavSheet
        open={sheet.open}
        onClose={() => setSheet({ open: false })}
        initialFocusId={sheet.focus}
      />
    </>
  );
}
